import type { FilterOptions } from "@/lib/movieList";
import { genres, regions, years } from "@/mocks/movies";

interface FilterPanelProps {
  options: FilterOptions;
  onChange: (next: Partial<FilterOptions>) => void;
  onReset: () => void;
  hideCategory?: boolean;
}

const sortOptions: { value: "heat" | "rating" | "year"; label: string }[] = [
  { value: "heat", label: "热度" },
  { value: "rating", label: "评分" },
  { value: "year", label: "年份" },
];

function Chip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
        active
          ? "border-primary-500 bg-primary-500 text-background-50"
          : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterPanel({ options, onChange, onReset, hideCategory }: FilterPanelProps) {
  const category = options.category ?? "all";
  const genre = options.genre ?? "全部";
  const region = options.region ?? "全部";
  const year = options.year ?? "all";
  const sort = options.sort ?? "heat";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <i className="ri-filter-3-line text-lg text-primary-600" />
          筛选条件
        </h3>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-500 transition-colors hover:text-accent-600"
        >
          <i className="ri-refresh-line" />
          清空
        </button>
      </div>

      {!hideCategory && (
        <div className="mb-5">
          <p className="mb-2.5 text-sm font-semibold text-foreground-700">影视类型</p>
          <div className="flex flex-wrap gap-2">
            <Chip active={category === "all"} label="全部" onClick={() => onChange({ category: "all" })} />
            <Chip active={category === "movie"} label="电影" onClick={() => onChange({ category: "movie" })} />
            <Chip active={category === "tv"} label="电视剧" onClick={() => onChange({ category: "tv" })} />
          </div>
        </div>
      )}

      <div className="mb-5">
        <p className="mb-2.5 text-sm font-semibold text-foreground-700">地区</p>
        <div className="flex flex-wrap gap-2">
          <Chip active={region === "全部"} label="全部" onClick={() => onChange({ region: "全部" })} />
          {regions.map((r) => (
            <Chip key={r} active={region === r} label={r} onClick={() => onChange({ region: r })} />
          ))}
        </div>
      </div>

      <div className="mb-5">
        <p className="mb-2.5 text-sm font-semibold text-foreground-700">年份</p>
        <div className="flex flex-wrap gap-2">
          <Chip active={year === "all"} label="全部" onClick={() => onChange({ year: "all" })} />
          {years.map((y) => (
            <Chip key={y} active={year === y} label={`${y}`} onClick={() => onChange({ year: y })} />
          ))}
        </div>
      </div>

      <div className="mb-5">
        <p className="mb-2.5 text-sm font-semibold text-foreground-700">类型题材</p>
        <div className="flex flex-wrap gap-2">
          <Chip active={genre === "全部"} label="全部" onClick={() => onChange({ genre: "全部" })} />
          {genres.map((g) => (
            <Chip key={g} active={genre === g} label={g} onClick={() => onChange({ genre: g })} />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2.5 text-sm font-semibold text-foreground-700">排序方式</p>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((s) => (
            <Chip key={s.value} active={sort === s.value} label={s.label} onClick={() => onChange({ sort: s.value })} />
          ))}
        </div>
      </div>
    </div>
  );
}